import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AlertTriangle, TrendingDown, Calendar, MessageSquare, User } from "lucide-react";
import { format, differenceInDays } from "date-fns";

interface AtRiskClientsWidgetProps {
  clinicianId: string;
  onViewClient?: (clientId: string) => void;
}

interface AtRiskClient {
  id: string;
  client_id: string;
  display_name: string;
  riskFactors: string[];
  riskLevel: 'high' | 'medium' | 'low';
  lastSession?: string;
  lastCheckIn?: string;
  moodTrend?: 'declining' | 'stable' | 'improving';
}

export function AtRiskClientsWidget({ clinicianId, onViewClient }: AtRiskClientsWidgetProps) {
  const { data: atRiskClients, isLoading } = useQuery({
    queryKey: ['at-risk-clients', clinicianId],
    queryFn: async () => {
      // Get active clients
      const { data: clients, error } = await supabase
        .from('clinician_clients')
        .select(`
          id,
          client_id,
          status
        `)
        .eq('clinician_id', clinicianId)
        .eq('status', 'active');

      if (error) throw error;
      if (!clients?.length) return [];

      const atRiskList: AtRiskClient[] = [];

      for (const client of clients) {
        const riskFactors: string[] = [];
        let riskLevel: 'high' | 'medium' | 'low' = 'low';

        // Get client profile
        const { data: profile } = await supabase
          .from('profiles')
          .select('display_name')
          .eq('id', client.client_id)
          .single();

        // Get last session
        const { data: lastSession } = await supabase
          .from('therapy_sessions')
          .select('session_date')
          .eq('client_id', client.client_id)
          .eq('clinician_id', clinicianId)
          .eq('status', 'completed')
          .order('session_date', { ascending: false })
          .limit(1)
          .maybeSingle();

        // Get last check-in
        const { data: lastCheckIn } = await supabase
          .from('daily_checkins')
          .select('created_at, energy_field')
          .eq('user_id', client.client_id)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        // Get recent mood data for trend (using energy_field as mood proxy)
        const { data: recentMoods } = await supabase
          .from('daily_checkins')
          .select('energy_field, created_at')
          .eq('user_id', client.client_id)
          .order('created_at', { ascending: false })
          .limit(7);

        // Get any active risk alerts
        const { data: riskAlerts } = await supabase
          .from('client_risk_alerts')
          .select('*')
          .eq('client_id', client.client_id)
          .eq('clinician_id', clinicianId)
          .eq('status', 'active');

        // Analyse risk factors
        if (lastSession) {
          const daysSinceSession = differenceInDays(new Date(), new Date(lastSession.session_date));
          if (daysSinceSession > 30) {
            riskFactors.push(`${daysSinceSession} days since last session`);
            if (daysSinceSession > 60) riskLevel = 'high';
            else riskLevel = 'medium';
          }
        } else {
          riskFactors.push('No sessions completed');
        }

        if (lastCheckIn) {
          const daysSinceCheckIn = differenceInDays(new Date(), new Date(lastCheckIn.created_at));
          if (daysSinceCheckIn > 7) {
            riskFactors.push(`${daysSinceCheckIn} days since check-in`);
            if (daysSinceCheckIn > 14) riskLevel = 'high';
          }
        }

        // Analyse mood trend (using energy_field as proxy: low/medium/high/very_high)
        let moodTrend: 'declining' | 'stable' | 'improving' | undefined;
        const energyToNumber = (e: string | null): number => {
          switch (e) {
            case 'very_high': return 4;
            case 'high': return 3;
            case 'medium': return 2;
            case 'low': return 1;
            default: return 2;
          }
        };
        if (recentMoods && recentMoods.length >= 3) {
          const recentAvg = recentMoods.slice(0, 3).reduce((sum, m) => sum + energyToNumber(m.energy_field), 0) / 3;
          const olderAvg = recentMoods.slice(-3).reduce((sum, m) => sum + energyToNumber(m.energy_field), 0) / Math.min(3, recentMoods.length);
          
          if (recentAvg < olderAvg - 0.5) {
            moodTrend = 'declining';
            riskFactors.push('Declining energy trend');
            riskLevel = riskLevel === 'low' ? 'medium' : 'high';
          } else if (recentAvg > olderAvg + 0.5) {
            moodTrend = 'improving';
          } else {
            moodTrend = 'stable';
          }
        }

        // Check for active risk alerts
        if (riskAlerts && riskAlerts.length > 0) {
          riskFactors.push(`${riskAlerts.length} active risk alert(s)`);
          riskLevel = 'high';
        }

        // Only add clients with risk factors
        if (riskFactors.length > 0) {
          atRiskList.push({
            id: client.id,
            client_id: client.client_id,
            display_name: profile?.display_name || 'Client',
            riskFactors,
            riskLevel,
            lastSession: lastSession?.session_date,
            lastCheckIn: lastCheckIn?.created_at,
            moodTrend
          });
        }
      }

      // Sort by risk level
      return atRiskList.sort((a, b) => {
        const levelOrder = { high: 0, medium: 1, low: 2 };
        return levelOrder[a.riskLevel] - levelOrder[b.riskLevel];
      });
    },
    staleTime: 5 * 60 * 1000 // 5 minutes
  });

  const getRiskBadge = (level: 'high' | 'medium' | 'low') => {
    switch (level) {
      case 'high':
        return <Badge className="bg-destructive/10 text-destructive border-destructive/20">High Risk</Badge>;
      case 'medium':
        return <Badge className="bg-amber-500/10 text-amber-500 border-amber-500/20">Medium Risk</Badge>;
      default:
        return <Badge className="bg-blue-500/10 text-blue-500 border-blue-500/20">Monitor</Badge>;
    }
  };

  if (isLoading) {
    return (
      <Card className="bg-card/50 border-border/30">
        <CardContent className="py-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
        </CardContent>
      </Card>
    );
  }

  const highRiskCount = atRiskClients?.filter(c => c.riskLevel === 'high').length || 0;

  return (
    <Card className="bg-card/50 border-border/30">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle className={`h-5 w-5 ${highRiskCount > 0 ? 'text-destructive' : 'text-primary'}`} />
            Clients Needing Attention
          </div>
          {highRiskCount > 0 && (
            <Badge className="bg-destructive text-destructive-foreground">{highRiskCount} urgent</Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!atRiskClients || atRiskClients.length === 0 ? (
          <div className="text-center py-6 text-muted-foreground">
            <AlertTriangle className="h-10 w-10 mx-auto mb-2 opacity-30" />
            <p>All clients are doing well!</p>
            <p className="text-sm">No concerning patterns detected</p>
          </div>
        ) : (
          <div className="space-y-3">
            {atRiskClients.slice(0, 5).map(client => (
              <div 
                key={client.id}
                className={`p-3 rounded-lg border ${
                  client.riskLevel === 'high' 
                    ? 'bg-destructive/5 border-destructive/20' 
                    : client.riskLevel === 'medium'
                    ? 'bg-amber-500/5 border-amber-500/20'
                    : 'bg-muted/30 border-border/30'
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="h-8 w-8 rounded-full bg-primary/20 flex items-center justify-center">
                      <User className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-foreground text-sm">{client.display_name}</p>
                      {client.moodTrend === 'declining' && (
                        <div className="flex items-center gap-1 text-xs text-destructive">
                          <TrendingDown className="h-3 w-3" />
                          Declining mood
                        </div>
                      )}
                    </div>
                  </div>
                  {getRiskBadge(client.riskLevel)}
                </div>
                
                <div className="space-y-1 mb-2">
                  {client.riskFactors.slice(0, 2).map((factor, i) => (
                    <p key={i} className="text-xs text-muted-foreground">• {factor}</p>
                  ))}
                  {client.riskFactors.length > 2 && (
                    <p className="text-xs text-muted-foreground">
                      + {client.riskFactors.length - 2} more
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  {client.lastSession && (
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {format(new Date(client.lastSession), 'MMM d')}
                    </span>
                  )}
                  {client.lastCheckIn && (
                    <span className="flex items-center gap-1">
                      <MessageSquare className="h-3 w-3" />
                      {format(new Date(client.lastCheckIn), 'MMM d')}
                    </span>
                  )}
                </div>

                {onViewClient && (
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    className="w-full mt-2 h-7 text-xs"
                    onClick={() => onViewClient(client.client_id)}
                  >
                    View Client
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
